import React from 'react';
import clsx from 'clsx';
import { CheckCircle2, Minus, Plus } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { formatQuantity } from '../../../utils/inventoryLabels';

/** How far a count is from what it has to reach. */
const progressState = (current, target, markComplete) => {
  if (current > target) return 'over';
  if (current === target && target > 0) return markComplete ? 'complete' : 'partial';
  return current > 0 ? 'partial' : 'empty';
};

/**
 * A quantity for one hand: big − and + buttons around the number, which can
 * also be typed with the phone's number pad.
 *
 * With a `target`, the number reads as how far the count is: «0 / 2», «1 / 2»,
 * green once it reaches it, amber past it. The count starts where the screen
 * says, at 0 for a count to complete.
 *
 * @param {object} props
 * @param {string|number} props.value
 * @param {(value: string) => void} props.onChange
 * @param {string} props.label
 * @param {number} [props.min=0]
 * @param {number} [props.max]
 * @param {boolean} [props.allowDecimals=false]
 * @param {string} [props.unit] - What is being counted, under the number.
 * @param {number} [props.target] - What the count has to reach.
 * @param {boolean} [props.markComplete=true] - Whether reaching `target` reads as done.
 * @param {string} [props.testId='quantity']
 * @param {React.Ref<HTMLInputElement>} [props.inputRef] - The number's field, for a screen to send the operator back to it.
 */
export const QuantityStepper = ({
  value,
  onChange,
  label,
  min = 0,
  max,
  allowDecimals = false,
  unit,
  target,
  markComplete = true,
  testId = 'quantity',
  inputRef,
}) => {
  const current = Number(value) || 0;
  const step = (delta) => {
    let next = current + delta;
    if (next < min) next = min;
    if (max != null && next > max) next = max;
    onChange(String(next));
  };

  const input = (wide) => (
    <input
      ref={inputRef}
      id={`${testId}-input`}
      data-testid={`${testId}-input`}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      inputMode={allowDecimals ? 'decimal' : 'numeric'}
      pattern={allowDecimals ? undefined : '[0-9]*'}
      enterKeyHint="done"
      autoComplete="off"
      size={wide ? undefined : Math.max(String(value ?? '').length, 1)}
      className={
        wide
          ? 'h-14 min-w-0 flex-1 rounded-2xl border border-border bg-ninja-gray text-center text-3xl font-bold text-ninja-dark outline-none focus:border-ninja-green dark:bg-[#111] dark:text-white'
          : 'min-w-0 bg-transparent text-right text-3xl font-bold outline-none'
      }
    />
  );

  const state = target != null ? progressState(current, target, markComplete) : null;

  return (
    <div className="space-y-2">
      <label htmlFor={`${testId}-input`} className="block text-center text-sm font-semibold text-ninja-dark dark:text-white">
        {label}
      </label>
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="lg"
          iconOnly
          icon={Minus}
          aria-label="Uno menos"
          disabled={current <= min}
          onClick={() => step(-1)}
          data-testid={`${testId}-decrease`}
        />
        {state ? (
          <div
            data-testid={`${testId}-progress`}
            data-state={state}
            className={clsx(
              'flex h-14 min-w-0 flex-1 items-center justify-center gap-2 rounded-2xl border bg-ninja-gray text-3xl font-bold focus-within:border-ninja-green dark:bg-[#111]',
              state === 'complete' && 'border-ninja-green text-ninja-green',
              state === 'over' && 'border-amber-400 text-amber-700 dark:border-amber-600 dark:text-amber-300',
              (state === 'empty' || state === 'partial') && 'border-border text-ninja-dark dark:text-white'
            )}
          >
            {input(false)}
            <span id={`${testId}-target`} className="shrink-0">
              / {formatQuantity(target)}
            </span>
            {state === 'complete' && <CheckCircle2 size={24} aria-hidden="true" className="shrink-0" />}
          </div>
        ) : (
          input(true)
        )}
        <Button
          variant="outline"
          size="lg"
          iconOnly
          icon={Plus}
          aria-label="Uno más"
          disabled={max != null && current >= max}
          onClick={() => step(1)}
          data-testid={`${testId}-increase`}
        />
      </div>
      {unit && <p className="text-center text-sm text-text-secondary">{unit}</p>}
    </div>
  );
};
